import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Book, ChevronRight, Search, FileText, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CollectionCopilotDrawer from '../components/copilot/CollectionCopilotDrawer';

/**
 * Slugify a heading string into an anchor ID.
 * "Section 1: The Inbox" -> "section-1-the-inbox"
 */
const slugifyHeading = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Extract text content from React children (handles nested elements).
 */
const extractText = (children) => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children?.props?.children) return extractText(children.props.children);
  return '';
};

/**
 * Custom heading components that add id attributes for anchor navigation.
 * Only used in the article renderer, NOT in the copilot markdown.
 */
const articleHeadingComponents = {
  h1: ({ children }) => {
    const id = slugifyHeading(extractText(children));
    return <h1 id={id}>{children}</h1>;
  },
  h2: ({ children }) => {
    const id = slugifyHeading(extractText(children));
    return <h2 id={id}>{children}</h2>;
  },
  h3: ({ children }) => {
    const id = slugifyHeading(extractText(children));
    return <h3 id={id}>{children}</h3>;
  },
  h4: ({ children }) => {
    const id = slugifyHeading(extractText(children));
    return <h4 id={id}>{children}</h4>;
  },
};

/**
 * Help & Documentation Page
 * 
 * Displays help articles organized by category.
 * Supports viewing individual articles with markdown rendering.
 * Includes a Support Copilot drawer with full documentation context.
 */
const HelpPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [categories, setCategories] = useState([]);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Help context from server (cached, pre-built with anchor catalogs)
  const [helpDocsContext, setHelpDocsContext] = useState('');
  const articleContentRef = useRef(null);

  // Load categories and help context on mount
  useEffect(() => {
    loadCategories();
    loadHelpContext();
  }, []);

  // Load article when slug changes
  useEffect(() => {
    if (slug) {
      loadArticle(slug);
    } else {
      setCurrentArticle(null);
    }
  }, [slug]);

  // Scroll to anchor after article loads
  useEffect(() => {
    if (currentArticle && location.hash) {
      // Wait for markdown to render
      const timer = setTimeout(() => {
        const anchorId = location.hash.slice(1);
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [currentArticle, location.hash]);

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/help/articles/categories');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHelpContext = async () => {
    try {
      const response = await api.get('/api/help/context', { params: { variant: 'internal' } });
      setHelpDocsContext(response.data?.context || '');
    } catch (err) {
      console.error('Failed to load help context:', err);
    }
  };

  const loadArticle = async (articleSlug) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/help/articles/${articleSlug}`);
      setCurrentArticle(response.data.article);
    } catch (err) {
      console.error('Failed to load article:', err);
      setCurrentArticle(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const response = await api.get(`/api/help/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.articles || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Render article list view (no article selected)
  const renderArticleList = () => (
    <div className="max-w-4xl mx-auto">
      {/* Search */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-20"
          />
          {searchQuery && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-16 top-1/2 -translate-y-1/2 h-7 px-2 text-xs"
            >
              Clear
            </Button>
          )}
          <Button
            type="submit"
            size="sm"
            disabled={searching || searchQuery.length < 2}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7"
          >
            Search
          </Button>
        </div>
      </form>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">
            Search Results ({searchResults.length})
          </h2>
          <div className="space-y-3">
            {searchResults.map((article) => (
              <Link
                key={article.slug}
                to={`/help/${article.slug}`}
                className="block p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                      {article.title}
                    </h3>
                    {article.summary && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {article.summary}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        {article.category}
                      </Badge>
                      {article.tags?.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
          <Separator className="my-8" />
        </div>
      )}

      {/* Categories */}
      <div className="space-y-8">
        {categories.map((category) => (
          <div key={category.category}>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Book className="w-5 h-5 text-indigo-500" />
              {category.category}
              <Badge variant="outline" className="ml-2">
                {category.count}
              </Badge>
            </h2>
            <div className="grid gap-3">
              {category.articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/help/${article.slug}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
                >
                  <FileText className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                  <span className="flex-1 text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">
                    {article.title}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        ))}

        {categories.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Book className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No documentation available yet.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render single article view
  const renderArticle = () => (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/help')}
        className="mb-6 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Documentation
      </Button>

      {currentArticle ? (
        <article ref={articleContentRef}>
          {/* Article header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline">{currentArticle.category}</Badge>
              {currentArticle.subcategory && (
                <>
                  <ChevronRight className="w-3 h-3 text-gray-400" />
                  <Badge variant="secondary">{currentArticle.subcategory}</Badge>
                </>
              )}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {currentArticle.title}
            </h1>
            {currentArticle.summary && (
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {currentArticle.summary}
              </p>
            )}
            {currentArticle.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {currentArticle.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </header>

          <Separator className="mb-8" />

          {/* Article content - with heading IDs for anchor navigation */}
          <div className="prose prose-gray dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 dark:prose-pre:bg-gray-950 dark:prose-pre:text-gray-100">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={articleHeadingComponents}>
              {currentArticle.content}
            </ReactMarkdown>
          </div>

          {/* Article footer */}
          <Separator className="my-8" />
          <footer className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {new Date(currentArticle.updated_at).toLocaleDateString()}
          </footer>
        </article>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Article not found.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/help')}
            className="mt-4"
          >
            Back to Documentation
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-1 min-h-0">
        {/* Main content area */}
        <div className="flex-1 min-w-0 p-8 overflow-y-auto">
          {/* Page header */}
          <div className="max-w-4xl mx-auto mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
              <Book className="w-7 h-7 text-indigo-500" />
              Help & Documentation
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Learn how to use StackRanked effectively
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="max-w-4xl mx-auto">
              <div className="animate-pulse space-y-4">
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </div>
          ) : slug ? (
            renderArticle()
          ) : (
            renderArticleList()
          )}
        </div>

        {/* Support Copilot Drawer */}
        <CollectionCopilotDrawer
          context="help"
          contextId="help_documents"
          title="Support Copilot"
          injectedContext={helpDocsContext}
        />
      </div>
    </div>
  );
};

export default HelpPage;
